
import React, { useState, useEffect } from 'react';
import { SUITE_DETAILS } from './constants';
import type { Page, NavParams, CustomIndustry, UserProfile } from '@types'; 
import { app, db } from '../../firebaseConfig';
import { collection, onSnapshot, query, where } from 'firebase/firestore';

interface KnowledgeHubProps {
    onNavigate: (nav: { page: Page; params?: NavParams }) => void;
    onLogout: () => void;
    customIndustries: CustomIndustry[]; 
    currentUser: UserProfile | null;
}

interface SuiteCardProps {
    name: string;
    details: {
        description: string;
        icon: React.ReactNode;
    };
    onClick: () => void;
    special?: boolean;
    frameworkStyle?: boolean;
}

const SuiteCard: React.FC<SuiteCardProps> = ({ name, details, onClick, special, frameworkStyle }) => (
    <div
        onClick={onClick}
        className={`relative group cursor-pointer bg-white p-5 rounded-xl border shadow-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 flex flex-col overflow-hidden 
        ${special ? 'border-blue-500 ring-2 ring-blue-100' : frameworkStyle ? 'border-emerald-500 ring-2 ring-emerald-100' : 'border-slate-200'}`}
    >
        {special && <div className="absolute top-0 right-0 bg-blue-600 text-white text-[10px] uppercase font-bold px-2 py-1 rounded-bl-lg">New Feature</div>}
        {frameworkStyle && <div className="absolute top-0 right-0 bg-emerald-600 text-white text-[10px] uppercase font-bold px-2 py-1 rounded-bl-lg">Active</div>}
        
        <div className="relative z-10">
            <div className="flex items-center mb-3">
                <div className={`p-2.5 rounded-lg ${special ? 'bg-blue-600 text-white' : frameworkStyle ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-blue-600 group-hover:bg-blue-100'} transition-colors`}>
                     {React.cloneElement(details.icon as React.ReactElement<any>, { className: 'h-6 w-6' })}
                </div>
                 <h3 className="ml-3 text-base font-bold text-slate-800">{name}</h3>
            </div>
           
            <p className="text-sm text-slate-600 flex-grow">{details.description}</p>
        </div>
        <div className="relative z-10 text-right mt-auto pt-4 h-5">
            <span className={`text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity ${special ? 'text-blue-700' : frameworkStyle ? 'text-emerald-700' : 'text-blue-600'}`}>
                Launch &rarr;
            </span>
        </div>
    </div>
);

const KnowledgeHub: React.FC<KnowledgeHubProps> = ({ onNavigate, onLogout, currentUser }) => {
    const [reportCount, setReportCount] = useState<number | null>(null);
    const [showClientModal, setShowClientModal] = useState(false);
    const [clientNameInput, setClientNameInput] = useState('');
    
    const welcomeVideoUrl = "https://videos.pexels.com/video-files/3209828/3209828-hd_1920_1080_25fps.mp4";
    const projectId = app?.options.projectId;

    const handlePurposeSelection = (purpose: 'consultant' | 'corporate' | 'agency') => {
        if (purpose === 'agency') {
            // Navigate directly to the full Agency Workspace
            onNavigate({ page: 'agencyDashboard' as Page });
        } else {
            onNavigate({ page: 'pillarsDashboard', params: { purpose } });
        }
    };

    // Legacy handler kept for any remaining modal references (now unused)
    const handleClientSubmit = (e: React.FormEvent) => {
        e.preventDefault();
    };

    useEffect(() => {
        // Ensure we don't run the query if currentUser or currentUser.uid is not available
        if (!db || !currentUser?.uid) {
            setReportCount(0); // Set to 0 if not logged in
            return;
        }

        // --- DEFINITIVE FIX ---
        // The query now includes a 'where' clause for the 'userId' to comply with Firestore security rules.
        const intelQuery = query(
            collection(db, 'saved_intel'), 
            where("userId", "==", currentUser.uid)
        );

        const unsubscribe = onSnapshot(intelQuery, (snapshot) => {
            setReportCount(snapshot.size);
        }, (error) => {
            console.error("Error listening to saved_intel collection:", error);
            setReportCount(null); // Set to null on error to indicate a problem
        });

        return () => unsubscribe();
    }, [currentUser]); // Depend on the entire currentUser object

    const allSuites = {
        ...SUITE_DETAILS,
        instructionManual: {
            name: 'Instruction Manual',
            description: 'Access the complete, in-depth user guide for the IntelOptics Insights platform.',
            icon: <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" /></svg>,
        },
    };

    const srmArchitectInfo = {
        name: 'Auto-Architect SRM Framework',
        description: 'Intelligently build a complete Security Risk Management framework. Provide your context, and Insights will assemble the required tools and structure automatically.',
        icon: <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" /></svg>
    };


    return (
        <div className="animate-fade-in">
            <div className="mb-6 bg-slate-900 text-white p-3 rounded-lg flex items-center justify-between shadow-sm border border-slate-700">
                <div className="flex items-center gap-3">
                    <div className="relative flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                    </div>
                    <span className="font-mono text-sm font-bold text-green-400">SYSTEM ONLINE</span>
                </div>
                <div className="text-xs text-slate-400 font-mono flex items-center gap-4">
                    <span>DATABASE: <span className="text-white">{projectId || 'LOCAL_MODE'}</span></span>
                    <div className="h-3 w-px bg-slate-600"></div>
                    <span>REPORTS ARCHIVED: <span className="text-white font-bold">{reportCount !== null ? reportCount : '-'}</span></span>
                </div>
            </div>

            <div className="bg-white text-slate-800 p-8 rounded-xl shadow-md grid grid-cols-1 lg:grid-cols-2 gap-8 items-center animate-fade-in-down mb-8 border border-slate-200">
                <div>
                    <h2 className="text-4xl font-bold mb-3 text-slate-900">Your Mission Control for Global Security and Geopolitical Risk Insights.</h2>
                    <p className="text-slate-600 leading-relaxed">
                        Welcome to IntelOptics Insights. This platform provides a comprehensive suite of Insights tools to help you anticipate risks, protect assets, and make mission-critical decisions with confidence. Select a specialized suite below to begin, or consult the **Instruction Manual** for detailed guidance.
                    </p>
                </div>
                
                <div className="bg-slate-900 rounded-lg border border-slate-700 text-center flex flex-col items-center justify-center min-h-[250px] lg:min-h-[320px] overflow-hidden relative p-6">
                    <video
                        src={welcomeVideoUrl}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="absolute top-0 left-0 w-full h-full object-cover z-0"
                    />
                </div>
            </div>

            {/* ENTERPRISE DASHBOARD SHORTCUT */}
            {currentUser?.organizationId && (
                <div className="mb-8 max-w-3xl mx-auto">
                    <SuiteCard
                        name="Enter Active Enterprise Dashboard"
                        details={{
                            description: 'Access the all-seeing Oracle dashboard to orchestrate your dedicated organizational intelligence, risk, and crisis resiliency programs.',
                            icon: <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-2.25h6V21" /></svg>,
                        }}
                        onClick={() => onNavigate({ page: 'organizationDashboard' })}
                        frameworkStyle
                    />
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <SuiteCard
                    name="Consultant (Individual Use)"
                    details={{
                        description: 'For independent security professionals managing personal workflows and intelligence gathering.',
                        icon: <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" /></svg>,
                    }}
                    onClick={() => handlePurposeSelection('consultant')}
                />
                <SuiteCard
                    name="Create Business Client Programs"
                    details={{
                        description: 'For agencies and MSSPs creating tailored security and resiliency programs for multiple external clients.',
                        icon: <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" /></svg>,
                    }}
                    onClick={() => handlePurposeSelection('agency')}
                    special
                />
                <SuiteCard
                    name="Create Corporate Program"
                    details={{
                        description: 'For in-house security directors managing enterprise-wide resiliency, risk, and compliance.',
                        icon: <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-2.25h6V21" /></svg>,
                    }}
                    onClick={() => handlePurposeSelection('corporate')}
                />
            </div>
            
            <div className="mt-8">
                <div className="flex border-t border-slate-200 mt-6 pt-6 justify-center">
                    <div className="w-full max-w-md">
                        <SuiteCard
                            name={allSuites.instructionManual.name}
                            details={allSuites.instructionManual}
                            onClick={() => onNavigate({ page: 'instructionManual' })}
                        />
                    </div>
                </div>
            </div>

            {showClientModal && (
                <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-fade-in-up">
                        <h3 className="text-xl font-bold text-slate-800 mb-2">Create New Client Program</h3>
                        <p className="text-sm text-slate-500 mb-6">Enter the name of the client to scope the workspace and tag generated reports.</p>
                        
                        <form onSubmit={handleClientSubmit}>
                            <div className="mb-4">
                                <label htmlFor="clientName" className="block text-sm font-semibold text-slate-700 mb-1">Business Client Name</label>
                                <input
                                    type="text"
                                    id="clientName"
                                    value={clientNameInput}
                                    onChange={(e) => setClientNameInput(e.target.value)}
                                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition-colors"
                                    placeholder="e.g., Acme Corporation"
                                    autoFocus
                                    required
                                />
                            </div>
                            
                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setShowClientModal(false)}
                                    className="px-4 py-2 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-sm transition-colors font-medium"
                                >
                                    Start Client Program
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            <div className="text-center mt-8">
                <button
                    onClick={onLogout}
                    className="bg-slate-600 hover:bg-slate-700 text-white font-bold py-3 px-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                >
                    Log Out & Return Home
                </button>
            </div>
        </div>
    );
};

export default KnowledgeHub;
